import { z } from "zod";
import { ORG_ROLES, type OrgRole } from "../enums";
import { LIMITS } from "../limits";
import { email, nonEmptyPatch, password, requiredText, uuid } from "./common";

// ---------- Requests ----------

export const signupBody = z
  .object({
    name: requiredText(LIMITS.userName, "Your name"),
    email,
    password,
    orgName: requiredText(LIMITS.orgName, "Organisation name"),
  })
  .strict();
export type SignupBody = z.infer<typeof signupBody>;

export const loginBody = z
  .object({
    email,
    password: z.string().min(1, "Enter your password.").max(LIMITS.passwordMax),
  })
  .strict();
export type LoginBody = z.infer<typeof loginBody>;

export const forgotPasswordBody = z.object({ email }).strict();
export type ForgotPasswordBody = z.infer<typeof forgotPasswordBody>;

export const resetPasswordBody = z
  .object({ token: z.string().min(10).max(200), password })
  .strict();
export type ResetPasswordBody = z.infer<typeof resetPasswordBody>;

export const updateMeBody = nonEmptyPatch({
  name: requiredText(LIMITS.userName, "Your name").optional(),
  notifyEmail: z.boolean().optional(),
  currentPassword: z.string().min(1).max(LIMITS.passwordMax).optional(),
  newPassword: password.optional(),
}).refine((v) => (v.currentPassword === undefined) === (v.newPassword === undefined), {
  message: "Enter your current password and a new password.",
  path: ["currentPassword"],
});
export type UpdateMeBody = z.infer<typeof updateMeBody>;

export const switchOrgBody = z.object({ orgId: uuid }).strict();
export type SwitchOrgBody = z.infer<typeof switchOrgBody>;

export const createOrgBody = z
  .object({ name: requiredText(LIMITS.orgName, "Organisation name") })
  .strict();
export type CreateOrgBody = z.infer<typeof createOrgBody>;

/** Signed out with no account: name + password. Signed in: `{}`. */
export const acceptInvitationBody = z
  .object({
    name: requiredText(LIMITS.userName, "Your name").optional(),
    password: password.optional(),
  })
  .strict();
export type AcceptInvitationBody = z.infer<typeof acceptInvitationBody>;

/** The part of the accept body needed to create a new account. */
export const acceptInvitationNewAccountBody = z
  .object({ name: requiredText(LIMITS.userName, "Your name"), password })
  .strict();

// ---------- Responses ----------

export type OrgMembershipRef = { id: string; name: string; role: OrgRole };

export type Me = {
  user: { id: string; email: string; name: string; notifyEmail: boolean };
  /** null = no active org (show "create organisation"). */
  org: OrgMembershipRef | null;
  /** Every membership, by name. */
  orgs: OrgMembershipRef[];
};

export type OkResponse = { ok: true };

export type InvitationPreview = {
  orgName: string;
  email: string;
  role: OrgRole;
  invitedByName: string;
  expiresAt: string;
  accountExists: boolean;
  signedInAs: string | null;
};

export const orgRole = z.enum(ORG_ROLES);
